import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './atoms/Button/Button';
import { Heading } from './atoms/Typography/Heading';
import { Text } from './atoms/Typography/Text';
import './ErrorModal.css';

/** Strip HTML tags from error messages to prevent raw markup appearing in the UI. */
function sanitizeMessage(msg: string): string {
  return msg.replace(/<[^>]*>/g, '');
}

interface ErrorModalProps {
  message: string;
  title?: string;
  onClose: () => void;
}

export function ErrorModal({ message, title = 'Error', onClose }: ErrorModalProps) {
  const safeMessage = sanitizeMessage(message);
  const okButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    // Store the currently focused element so we can restore focus on close
    previousFocusRef.current = document.activeElement;

    // Move focus to the OK button immediately on mount
    okButtonRef.current?.focus();

    return () => {
      // Restore focus to the previously focused element on unmount
      if (
        previousFocusRef.current instanceof HTMLElement ||
        previousFocusRef.current instanceof SVGElement
      ) {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key === 'Tab') {
        // Focus trap: query all focusable elements inside the modal
        const modal = document.getElementById('error-modal-dialog');
        if (!modal) return;

        const focusableSelectors =
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        const focusableElements = Array.from(
          modal.querySelectorAll<HTMLElement>(focusableSelectors),
        ).filter((el) => !el.hasAttribute('disabled'));

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          // Shift+Tab: if at first element, cycle to last
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement?.focus();
          }
        } else {
          // Tab: if at last element, cycle to first
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement?.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const titleId = 'error-modal-title';
  const descId = 'error-modal-description';

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Only close if the click is directly on the backdrop, not on the dialog
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const portalTarget = document.getElementById('error-root');

  if (!portalTarget) {
    // Fallback: render nothing if portal target doesn't exist
    return null;
  }

  const modal = (
    // Backdrop click is a pointer convenience; Escape is the keyboard equivalent.
    <div
      className="error-modal__overlay"
      role="presentation"
      onClick={handleBackdropClick}
    >
      <div
        id="error-modal-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="error-modal__dialog"
      >
        <Heading level={2} id={titleId} className="error-modal__title">
          {title}
        </Heading>
        <Text id={descId} className="error-modal__message">
          {safeMessage}
        </Text>
        <div className="error-modal__actions">
          <Button
            ref={okButtonRef}
            variant="primary"
            onClick={onClose}
            className="error-modal__ok"
          >
            OK
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, portalTarget);
}
